import React, {PropsWithChildren, useCallback, useMemo, useRef} from 'react';
import {Item, Menu, RightSlot, Separator, useContextMenu} from 'react-contexify';
import {Icon, IconifyIcon} from '@iconify/react';
import classNames from 'classnames';
import './contextMenu.scss';

const CONTEXT_MENU_Y_OFFSET = 150;
const FALLBACK_ICON = 'ph:circle';

export type ContextMenuItem = {
    label: string;
    icon?: string | IconifyIcon;
    iconStyles?: React.CSSProperties;
    onClick: () => void;
    disabled: boolean;
    shortcuts?: string[];
};

interface ContextMenuTriggerProps {
    menuId: string;
    offsetY?: boolean;
    menuProps?: Record<string, unknown>;
}

interface ContextMenuPortalProps {
    menuId: string;
    items: ContextMenuItem[][];
}

interface ContextMenuProps {
    menuId: string;
    items: ContextMenuItem[][];
    offsetY?: boolean;
}

export function formatShortcuts(shortcuts?: string[]): JSX.Element | null {
    if (!shortcuts) return null;

    return (
        <div className="shortcut-container u-ml-2">
            {shortcuts.map((key, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <span className="shortcut-plus">+</span>}
                    <span className="shortcut-key">{key.toUpperCase()}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

export function createKeyMatcher(shortcuts?: string[]): ((e: KeyboardEvent) => boolean) | undefined {
    if (!shortcuts) return undefined;

    return (e: KeyboardEvent): boolean =>
        shortcuts.every((shortcut) => {
            if (shortcut.toLocaleLowerCase() === 'ctrl' && e.metaKey) return true;
            return e.key.toLowerCase() === shortcut.toLocaleLowerCase();
        });
}

export function ContextMenuTrigger({menuId, offsetY, menuProps, children}: PropsWithChildren<ContextMenuTriggerProps>): JSX.Element {
    const {show, hideAll} = useContextMenu({id: menuId});
    const menuPropsRef = useRef(menuProps);
    menuPropsRef.current = menuProps;

    const handleContextMenu = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            event.stopPropagation();

            show({
                id: menuId,
                event,
                props: menuPropsRef.current,
                position: offsetY ? {x: event.clientX, y: event.clientY - CONTEXT_MENU_Y_OFFSET} : null,
            });
        },
        [menuId, offsetY, show]
    );

    const handleMouseDown = useCallback((): void => {
        hideAll();
    }, [hideAll]);

    return (
        <span onContextMenu={handleContextMenu} onMouseDown={handleMouseDown}>
            {children}
        </span>
    );
}

export function ContextMenuPortal({menuId, items}: ContextMenuPortalProps): JSX.Element {
    const menuItems = useMemo(
        () =>
            items.map((group, index) => (
                <React.Fragment key={index}>
                    {group.map((item, itemIndex) => (
                        <Item key={itemIndex} onClick={item.onClick} disabled={item.disabled} keyMatcher={createKeyMatcher(item.shortcuts)}>
                            <Icon
                                className={classNames('contexify_itemContent__icon', {'icon--hidden': !item.icon})}
                                style={item.iconStyles}
                                icon={item.icon ?? FALLBACK_ICON}
                            />
                            <span className="contexify_itemContent__label">{item.label}</span>
                            <RightSlot>{formatShortcuts(item.shortcuts)}</RightSlot>
                        </Item>
                    ))}
                    {index < items.length - 1 && <Separator />}
                </React.Fragment>
            )),
        [items]
    );

    return <Menu id={menuId}>{menuItems}</Menu>;
}

function ContextMenu({menuId, items, offsetY, children}: PropsWithChildren<ContextMenuProps>): JSX.Element {
    return (
        <>
            <ContextMenuPortal menuId={menuId} items={items} />
            <ContextMenuTrigger menuId={menuId} offsetY={offsetY}>
                {children}
            </ContextMenuTrigger>
        </>
    );
}

export default ContextMenu;
